import type { SortDirection } from 'app/generated/graphql'
import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { convertUrlFiltersToES } from 'app/filter.utils'
import { VOICE_AGENT_REMOVED_FILTER_ATTRIBUTES } from 'app/components/ai-agent-voice/useAiAgentVoice'
import { useAddMarketingColumns } from 'app/hooks/useAddMarketingColumns'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { useActivityDrawer } from 'app/hooks/useActivityDrawer'
import type { ActivityJson, ContactJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { SearchResponse } from 'app/services/search'
import { bg } from 'lib/background'
import { useActivityStore, useContactStore } from 'app/stores/StoreContext'
import { aiAgentColumns, getAiAgentFilterableColumns, withAiAgentDefaultVisibility } from 'app/table-columns/activities'
import { logError } from 'lib/logging'
import { usPhoneNumber } from 'lib/phone'

export type UseAiAgentCallLogReturn = {
  activities: SearchResponse<ActivityJson> | undefined
  columns: ReturnType<typeof aiAgentColumns>
  memoizedFilters: ReturnType<typeof getAiAgentFilterableColumns>
  initialSort: any
  drawer: ReturnType<typeof useActivityDrawer>
  loadActivity: (id: string) => Promise<void>
  onOwnerUpdate: (activityId: string, owner?: ActivityJson['owner']) => Promise<void>
  search: string | undefined
  focusedContact: ContactJson | undefined
  openContactDrawer: boolean
  handleContactClick: (contactId: string) => Promise<void>
  closeContactDrawer: () => void
  loadMore: () => Promise<unknown>
}

const PAGE_SIZE = 50

export function useAiAgentCallLog(): UseAiAgentCallLogReturn {
  const [searchParams] = useSearchParams()
  const addMarketingColumns = useAddMarketingColumns()
  const sort = searchParams.get(`sort`) === `` ? undefined : (searchParams.get(`sort`) ?? `dateTime`)
  const initialSort = useInitialTableSort(sort)

  const query = searchParams.get(`query`)
  const sortParts = sort?.split(`:`)

  // Normalize a bare 10-digit phone query into a field-scoped clause: formatted
  // numbers like "(608) 467-0732" contain query_string syntax characters and
  // match nothing when joined into the search raw. See TRK-5304.
  const queryDigits = usPhoneNumber(query ?? ``)
  const searchQueryTerm = queryDigits
    ? `(contactPhone:${queryDigits} OR phoneFromActivity:${queryDigits} OR contacts.phone:${queryDigits})`
    : query

  // Shared date/location filter (written by the page's DashboardHeader). Defaults
  // to the current month to match the Voice Agent Dashboard tab.
  const fromDate = searchParams.get(`fromDate`) || moment().startOf(`month`).format(`YYYY-MM-DD`)
  const toDate = searchParams.get(`toDate`) || moment().endOf(`month`).format(`YYYY-MM-DD`)
  const locationIds = compact(searchParams.get(`locationIds`)?.split(`,`) ?? [])

  const memoizedFilters = useMemo(
    () =>
      getAiAgentFilterableColumns({
        comActivityTypes: [],
        addMarketingColumns,
      }).filter((col) => !VOICE_AGENT_REMOVED_FILTER_ATTRIBUTES.has(col.attribute)),
    [addMarketingColumns],
  )

  const filterTerms = convertUrlFiltersToES(searchParams.get(`filters`), undefined, memoizedFilters)

  const search =
    compact([
      searchQueryTerm,
      `type:Call AND tags:AI`,
      `activityDate:[${fromDate} TO ${toDate}]`,
      locationIds.length ? `locationId:(${locationIds.join(` OR `)})` : undefined,
      ...filterTerms,
    ]).join(` AND `) || undefined

  const sortInput =
    sortParts?.length === 2
      ? { field: sortParts[0], direction: sortParts[1].toUpperCase() as SortDirection }
      : undefined
  const activityStore = useActivityStore()
  const listKey = JSON.stringify({ search, sortInput })
  const [loadedKey, setLoadedKey] = useState<string>()
  useEffect(() => {
    let superseded = false
    bg(
      activityStore.fetchList({ search, pagination: { page: 0, limit: PAGE_SIZE }, sort: sortInput }).then(() => {
        if (!superseded) setLoadedKey(listKey)
      }),
    )
    return () => {
      superseded = true
    }
  }, [listKey])
  // The store's list is shared with the Activities page, so it is shown only once it holds this search.
  const activities =
    loadedKey === listKey ? (activityStore.tableData as SearchResponse<ActivityJson> | undefined) : undefined

  const drawer = useActivityDrawer(`call-log`)

  async function onOwnerUpdate(activityId: string, owner?: ActivityJson['owner']): Promise<void> {
    const result = await activityStore.reassignOwner(activityId, owner)
    if (result.error) {
      logError(`Error updating activity owner`, result.error.message)
      notifyError(`Error updating activity owner`)
      return
    }
    notifySuccess(`Staff updated successfully`)
  }

  async function loadActivity(id: string): Promise<void> {
    const result = await activityStore.fetchOne(id)
    if (result.error || !result.data) {
      logError(`Error loading activity`, result.error?.message)
      notifyError(`Activity not saved ${result.error?.message ?? ``}`)
      return
    }
    drawer.setFocusedActivity(activityStore.current as unknown as ActivityJson)
  }

  // Contact drawer opened from the table's contact chips and, in agent-only workspaces, from
  // the activity drawer's contact card, where it is the read-only CRM preview. See TRK-5944.
  const contactStore = useContactStore()
  const [focusedContact, setFocusedContact] = useState<ContactJson>()
  const [openContactDrawer, setOpenContactDrawer] = useState(false)

  async function handleContactClick(contactId: string): Promise<void> {
    drawer.setOpenDrawer(false)
    const result = await contactStore.fetchOne(contactId)
    const contact = (result.data as { contact?: ContactJson } | undefined)?.contact
    if (!contact || result.error) {
      logError(`Error loading contact`, result.error?.message)
      notifyError(`Error loading contact`)
      return
    }
    setFocusedContact(contact)
    setOpenContactDrawer(true)
  }

  function closeContactDrawer(): void {
    setFocusedContact(undefined)
    setOpenContactDrawer(false)
    drawer.closeDrawer()
  }

  const columns = useMemo(
    () =>
      withAiAgentDefaultVisibility(
        aiAgentColumns(
          addMarketingColumns,
          drawer.baseUrl,
          drawer.selectActivity,
          onOwnerUpdate,
          true,
          handleContactClick,
        ),
      ),
    [drawer.focusedActivity, drawer.baseUrl],
  )

  return {
    activities,
    columns,
    memoizedFilters,
    initialSort,
    drawer,
    loadActivity,
    onOwnerUpdate,
    search,
    focusedContact,
    openContactDrawer,
    handleContactClick,
    closeContactDrawer,
    loadMore: activityStore.fetchNextPage,
  }
}
